"use client";
import { useCleverTapEvents } from "@/app/hooks/use-clevertap";
import { UEListData } from "@/app/interface/homepage";
import UEHeroCard from "@/app/unlisted-shares/components/ue-hero-card";
import { setAnalyticsEvent } from "@/app/utils/SetAnalyticsEvent";
// import { initBranch, trackEvent } from '@/app/utils/branchio';
import { buildKycRedirectUrl } from "@/app/utils/kyc-routing";
import { ButtonSize, ButtonVariant, RegularButton } from "@/stories/button/button";
import { useRouter } from "next/navigation";
import React from "react";

interface RewardPreKYCSectionProps {
    isUELoading?: boolean;
    data: UEListData[];
    isKycDone: boolean;
    title?: string;
    description?: string;
    referrerEmail?: string;
    userId?: string;
    phone?: string;
    isLoading?: boolean;
    kycFlow?: string;
}

const RewardPreKYCSection: React.FC<RewardPreKYCSectionProps> = ({
    isUELoading,
    data,
    title,
    description,
    isKycDone,
    referrerEmail,
    userId,
    phone,
    isLoading,
    kycFlow,
}) => {
    const router = useRouter();
    const { cleverTapTrackEvent } = useCleverTapEvents();

    const exploreUeOpportunities = () => {
        cleverTapTrackEvent("US_Rewards_Explore_Unlisted_Shares", {
            email: referrerEmail,
            phone,
            userId,
        });
        // initBranch()
        //     .then(() => {
        //         trackEvent("US_Rewards_Explore_Unlisted_Shares", referrerEmail, phone, userId);
        //     })
        //     .catch(error => console.error('Error initializing Branch:', error))

        setAnalyticsEvent(window.location.origin, "US_Rewards_Explore_Unlisted_Shares");
        window.location.href = `${process.env.ICM_UE_POST_URL_LISTING}` || "/";
    };

    if (isLoading) {
        return (
            <div className="mx-auto flex w-full flex-col items-center justify-center md:w-[910px] md:gap-[54px]">
                <div className="mx-6 my-8 flex flex-col items-center justify-center gap-5 text-center md:mx-0 md:my-14 md:mb-0 md:mt-6">
                    <div className="h-10 w-3/4 animate-pulse rounded bg-gray-200 md:h-16" />
                    <div className="h-6 w-2/3 animate-pulse rounded bg-gray-100 md:h-8" />
                </div>
                <div className="grid grid-cols-1 gap-[8px] md:grid-cols-2 md:gap-4">
                    {Array.from({ length: 4 }).map((_, index) => (
                        <div
                            key={index}
                            className="w-[300px] animate-pulse rounded-2xl border-2 border-gray-200 p-4 md:w-[442px] md:p-6"
                        >
                            <div className="flex gap-4 bg-gray-200">
                                <div className="h-12 w-12 flex-shrink-0 rounded bg-gray-300 md:h-20 md:w-20"></div>
                                <div className="flex min-w-0 flex-1 items-center justify-between">
                                    <div className="flex min-w-0 max-w-[calc(100%-40px)] flex-col gap-2">
                                        <div className="h-4 w-24 rounded bg-gray-300"></div>
                                        <div className="h-3 w-16 rounded bg-gray-200"></div>
                                    </div>
                                    <div className="h-8 w-20 rounded bg-gray-300"></div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="flex w-full justify-center px-10 md:px-0">
                    <div className="mt-8 h-12 w-full max-w-xs rounded bg-gray-200 md:mt-0"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="mx-auto flex w-full flex-col items-center justify-center md:w-[910px] md:gap-[54px]">
            <div className="mx-6 my-8 flex flex-col items-center justify-center gap-5 text-center md:mx-0 md:my-14 md:mb-0 md:mt-6">
                <div
                    className="mx-auto mb-3 max-w-[828px] text-4xl font-semibold leading-[41.6px] tracking-[-0.48px] text-neutral-900 md:mb-0 md:text-7xl md:leading-[67px] md:tracking-[-0.56px]"
                    dangerouslySetInnerHTML={{ __html: title || "" }}
                />
                <div
                    className="mx-auto text-md leading-[25.6px] tracking-[-0.2px] text-neutral-500 md:max-w-[872px] md:text-xl md:leading-[32px]"
                    dangerouslySetInnerHTML={{ __html: description || "" }}
                />
            </div>
            <div className="grid grid-cols-1 gap-[8px] md:grid-cols-2 md:gap-4">
                {isUELoading
                    ? Array.from({ length: 4 }).map((_, index) => (
                          <div
                              key={index}
                              className="bg-red w-[300px] animate-pulse rounded-2xl border-2 border-gray-200 p-4 md:w-[442px] md:p-6"
                          >
                              <div className="flex gap-4 bg-gray-200">
                                  {/* Image container with fixed dimensions */}
                                  <div className="h-12 w-12 flex-shrink-0 md:h-20 md:w-20"></div>

                                  {/* Content wrapper with proper flex behavior */}
                                  <div className="flex min-w-0 flex-1 items-center justify-between">
                                      {/* Text content with proper truncation */}
                                      <div className="flex min-w-0 max-w-[calc(100%-40px)] flex-col gap-2">
                                          <div className="overflow-hidden">
                                              <h3 className="truncate text-sm font-semibold md:text-lg"></h3>
                                          </div>
                                          <div className="whitespace-nowrap text-sm">
                                              <span className="text-gray-400"></span>
                                          </div>
                                      </div>

                                      {/* Button with fixed width */}
                                      <div className="flex-shrink-0"></div>
                                  </div>
                              </div>
                          </div>
                      ))
                    : data?.map((card: UEListData, index: number) => (
                        <UEHeroCard
                            key={index}
                            {...card}
                            refEvent={`US_Rewards_${card.product}`}
                            email={referrerEmail}
                            userId={userId}
                            phone={phone}
                        />
                    ))}
            </div>
            <div className="px-10 md:px-0">
                <RegularButton
                    noIcon
                    showRightIcon={false}
                    size={ButtonSize.Large}
                    variant={ButtonVariant.PrimaryDefault}
                    className="mt-8 w-full max-w-full md:mt-0 md:w-auto"
                    label={isKycDone ? "Explore Unlisted Shares" : "Start KYC"}
                    onClick={() =>
                        isKycDone
                            ? exploreUeOpportunities()
                            : router.push(
                                buildKycRedirectUrl({
                                    kycFlow,
                                    redirectUrl: "/refer",
                                    baseUrl: process.env.ICM_URL,
                                }),
                            )
                    }
                />
            </div>
        </div>
    );
};

export default RewardPreKYCSection;
